import { CardBody } from 'components';
import { ApiProperty } from '../types/Add-subscription.types';
import './PropertyList.scss';

export interface YearRange {
  minYear: number;
  maxYear: number;
}

interface PropertyListProps {
  /** Lista de propiedades (ya cargada; si no se pasa, no se muestra select) */
  list: ApiProperty[];
  /** undefined = sin elegir (muestra "Elige una opción"), null = crear nueva, ApiProperty = vehículo existente */
  selectedProperty: ApiProperty | null | undefined;
  onSelectProperty: (property: ApiProperty | null | undefined) => void;
  /** Etiqueta del producto para este slot (ej. "Autos nuevos - Vehículo 1") */
  productLabel?: string;
  /** Si hay error al cargar la lista (cuando la lista viene de fuera) */
  loading?: boolean;
  error?: boolean;
  /** Rango de años permitido para este price (el año del vehículo debe estar entre maxYear y minYear) */
  yearRange?: YearRange | null;
  /** Si se pasa, los vehículos que ya tienen este producto se muestran deshabilitados con tooltip */
  stripePriceId?: string;
  /** Cuando true, no se muestra el header (título/descripción); útil cuando se embebe bajo un título ya mostrado */
  hideHeader?: boolean;
}

/** Devuelve si el vehículo es elegible por año y el mensaje para tooltip si no lo es */
export function getYearEligibility(
  property: ApiProperty,
  yearRange?: YearRange | null
): { eligible: boolean; tooltip: string } {
  const yearStr = property.year?.trim();
  const vehicleYear = yearStr ? parseInt(yearStr, 10) : NaN;
  if (!yearStr || isNaN(vehicleYear) || vehicleYear <= 0) {
    return {
      eligible: false,
      tooltip: 'Se necesita año para ser seleccionado',
    };
  }
  if (yearRange) {
    const { minYear, maxYear } = yearRange;
    if (vehicleYear < maxYear || vehicleYear > minYear) {
      return {
        eligible: false,
        tooltip: `El año del vehículo no cumple con el rango permitido para este producto (años ${maxYear} a ${minYear})`,
      };
    }
  }
  return { eligible: true, tooltip: '' };
}

function getOptionLabel(property: ApiProperty): string {
  const parts: string[] = [];
  if (property.plate) parts.push(`Placa ${property.plate}`);
  if (property.make) parts.push(property.make);
  if (property.model) parts.push(property.model);
  if (property.year) parts.push(String(property.year));
  const typeLabel =
    (property.type || '').toLowerCase() === 'motocicleta'
      ? 'Motocicleta'
      : 'Vehículo';
  return [typeLabel, ...parts].filter(Boolean).join(' · ');
}

const VALUE_EMPTY = '__empty__';
const VALUE_NEW = '__new__';

export function PropertyList({
  list,
  selectedProperty,
  onSelectProperty,
  productLabel = 'Vehículo',
  loading = false,
  error = false,
  yearRange,
  stripePriceId,
  hideHeader = false,
}: PropertyListProps) {
  const hasProduct = (p: ApiProperty) =>
    Boolean(
      stripePriceId &&
        p.linked?.some((l) => l.userProductStripePriceId === stripePriceId)
    );

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === VALUE_EMPTY) {
      onSelectProperty(undefined);
      return;
    }
    if (value === VALUE_NEW) {
      onSelectProperty(null);
      return;
    }
    const property = list.find((p) => p.uid === value);
    if (!property) return;
    if (hasProduct(property)) return;
    const { eligible } = getYearEligibility(property, yearRange);
    if (!eligible) return;
    onSelectProperty(property);
  };

  const selectValue =
    selectedProperty === undefined
      ? VALUE_EMPTY
      : selectedProperty === null
      ? VALUE_NEW
      : list.some((p) => p.uid === selectedProperty.uid) &&
        !hasProduct(selectedProperty) &&
        getYearEligibility(selectedProperty, yearRange).eligible
      ? selectedProperty.uid
      : VALUE_EMPTY;

  const descriptionMessage = loading
    ? 'Cargando vehículos...'
    : error
    ? 'No se pudieron cargar los vehículos.'
    : 'Elige un vehículo existente o crea uno nuevo';

  const content = (
    <>
      {!hideHeader && (
        <header className="property-list__header">
          <h2 className="property-list__title">{productLabel}</h2>
          <p
            className={
              error
                ? 'property-list__description property-list__description--error'
                : 'property-list__description'
            }
          >
            {descriptionMessage}
          </p>
        </header>
      )}
      {hideHeader && (loading || error) && (
        <p
          className={
            error
              ? 'property-list__description property-list__description--error'
              : 'property-list__description'
          }
        >
          {descriptionMessage}
        </p>
      )}
      {!loading && !error && (
        <>
          <div className="property-list__select-wrap">
            <select
              className="property-list__select"
              value={selectValue}
              onChange={handleChange}
              aria-label={`Seleccionar vehículo para ${productLabel}`}
            >
              <option value={VALUE_EMPTY}>Elige una opción</option>
              <option value={VALUE_NEW}>Crear nueva propiedad</option>
              {list.map((property) => {
                const alreadyHasProduct = hasProduct(property);
                const { eligible: yearEligible, tooltip: yearTooltip } =
                  getYearEligibility(property, yearRange);
                const disabled = alreadyHasProduct || !yearEligible;
                const tooltip = alreadyHasProduct
                  ? 'Este vehículo ya tiene este producto asociado'
                  : !yearEligible
                  ? yearTooltip
                  : undefined;
                return (
                  <option
                    key={property.uid}
                    value={property.uid}
                    disabled={disabled}
                    title={tooltip}
                  >
                    {getOptionLabel(property)}
                  </option>
                );
              })}
            </select>
          </div>
        </>
      )}
    </>
  );

  if (loading && !hideHeader) {
    return <CardBody>{content}</CardBody>;
  }
  if (error && !hideHeader) {
    return <CardBody>{content}</CardBody>;
  }
  if (hideHeader) {
    return (
      <div className="property-list property-list--embedded">{content}</div>
    );
  }
  return <CardBody>{content}</CardBody>;
}
